/*!
 * Copyright © 2026 Nail Alliance - North America, Inc.
 * Authors: See snippets/copyright-notices.liquid in the theme.
 * No other use is authorized without a written contract.
 */

if (!customElements.get('virtual-salon')) customElements.define('virtual-salon', class extends HTMLElement {
    async connectedCallback() {
        this.controller = new AbortController();
        this.cache = new Map();
        this.paintVersion = 0;
        this.canvas = this.querySelector('[data-canvas]');
        this.status = this.querySelector('[data-status]');
        const params = new URL(location.href).searchParams;
        this.shape = ['square', 'round', 'oval', 'point'].includes(params.get('shape')) ? params.get('shape') : 'square';
        this.querySelector('[data-length]').value = Math.max(1, Math.min(3, Number(params.get('length')) || 1));
        this.querySelector('[data-skin]').value = Math.max(0, Math.min(100, Number(params.get('skin')) || 0));
        try {
            const response = await fetch(this.dataset.source, {signal: this.controller.signal});
            if (!response.ok) throw new Error('Unavailable');
            this.data = await response.json();
            this.selected = this.data.products.find(p => p.sku === params.get('sku')) || this.data.products.find(p => p.sku === '1110867') || this.data.products[0];
            Object.keys(this.data.map.collections).forEach(value => this.querySelector('[data-season]').add(new Option(value.replace(/(\d)/, ' $1'), value)));
            this.addEventListener('change', () => this.filter(), {signal: this.controller.signal});
            this.querySelectorAll('[data-length],[data-skin]').forEach(input => input.addEventListener('input', () => this.paint(), {signal: this.controller.signal}));
            this.querySelectorAll('[data-shape]').forEach(button => button.addEventListener('click', () => {
                this.shape = button.dataset.shape;
                this.paint();
            }, {signal: this.controller.signal}));
            this.querySelector('[data-share]').addEventListener('click', async () => {
                this.updateURL();
                try {
                    await navigator.clipboard.writeText(location.href);
                    this.status.textContent = 'Look link copied.';
                } catch {
                    this.status.textContent = 'Copy the address from your browser to share this look.';
                }
            }, {signal: this.controller.signal});
            this.filter();
            await this.paint();
        } catch (error) {
            if (error.name !== 'AbortError') this.status.textContent = 'The virtual salon could not load. Please refresh to try again.';
        }
    }

    filter() {
        const core = this.querySelector('[data-catalog]').value === 'core';
        this.querySelector('[data-seasons-label]').hidden = core;
        this.querySelector('[data-family]').parentElement.hidden = !core;
        this.querySelector('[data-texture-label]').hidden = !core;
        const skus = core ? this.data.map.core[this.querySelector('[data-family]').value] : this.data.map.collections[this.querySelector('[data-season]').value];
        const textures = this.data.textures[this.querySelector('[data-texture]').value] || [];
        const matches = this.data.products.filter(product => (skus || []).includes(product.sku) && (!core || textures.includes(product.sku)));
        const swatches = this.querySelector('[data-swatches]');
        swatches.replaceChildren();
        for (const product of matches) {
            const button = document.createElement('button');
            button.type = 'button';
            button.dataset.sku = product.sku;
            button.setAttribute('aria-label', product.name || `Shade ${product.sku}`);
            button.setAttribute('aria-pressed', String(product === this.selected));
            const img = document.createElement('img');
            img.src = this.url(product.swatch);
            img.alt = '';
            img.loading = 'lazy';
            button.append(img);
            button.addEventListener('click', () => {
                this.selected = product;
                this.paint();
            }, {signal: this.controller.signal});
            swatches.append(button);
        }
        if (!matches.length) swatches.textContent = 'No shades in this combination. Try another family or finish.';
    }

    url(asset) {
        return new URL(asset, new URL(this.dataset.source, document.baseURI)).href;
    }

    load(asset) {
        if (!this.cache.has(asset)) this.cache.set(asset, new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = this.url(asset);
        }));
        return this.cache.get(asset);
    }

    async paint() {
        if (!this.selected) return;
        const version = ++this.paintVersion;
        const length = this.querySelector('[data-length]').value;
        const skin = Number(this.querySelector('[data-skin]').value);
        this.querySelectorAll('[data-shape]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.shape === this.shape)));
        this.querySelectorAll('[data-swatches] button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sku === this.selected.sku)));
        const selected = this.selected;
        const nailAssets = this.data.hand.nails[this.shape][length];
        try {
            const [palms, fingers, swatch, nailShape, nailLight, nailShadow, bottleMask, bottleLight] = await Promise.all([
                Promise.all(this.data.hand.palms.map(p => this.load(p))), Promise.all(this.data.hand.fingers.map(p => this.load(p))), this.load(selected.swatch), this.load(nailAssets.shapes), this.load(nailAssets.highlights), this.load(nailAssets.shadows), this.load(this.data.hand.bottle.dotAndNeck), this.load(this.data.hand.bottle.highlights)
            ]);
            if (version !== this.paintVersion || !this.isConnected) return;
            const make = () => {
                const c = document.createElement('canvas');
                c.width = 1556;
                c.height = 1062;
                return c;
            };
            const nails = make(), bottle = make(), frame = make();
            const stamp = (context, x, y, angle) => {
                context.save();
                context.translate(x * 2, y * 2);
                context.rotate(angle * Math.PI / 180);
                context.drawImage(swatch, 0, 0, 440, 600);
                context.restore();
            };
            const nailContext = nails.getContext('2d');
            [[421.61, -26.77, 48], [515.11, 87.565, 49], [615, 203.065, 54], [719.445, 309.065, 63]].forEach(coords => stamp(nailContext, ...coords));
            nailContext.globalCompositeOperation = 'destination-in';
            nailContext.drawImage(nailShape, 0, 0, 1556, 1062);
            nailContext.globalCompositeOperation = 'source-over';
            nailContext.drawImage(nailLight, 0, 0, 1556, 1062);
            const bottleContext = bottle.getContext('2d');
            stamp(bottleContext, 335, 30, 55);
            stamp(bottleContext, 315, 369.5, 324);
            bottleContext.globalCompositeOperation = 'destination-in';
            bottleContext.drawImage(bottleMask, 0, 0, 1556, 1062);
            bottleContext.globalCompositeOperation = 'source-over';
            bottleContext.drawImage(bottleLight, 0, 0, 1556, 1062);
            const context = frame.getContext('2d');
            const opacity = 5 * (100 - skin) / 100, index = Math.floor(opacity) + 1,
                fraction = opacity - Math.floor(opacity);
            const drawSkin = images => images.forEach((img, i) => {
                context.globalAlpha = i === index ? fraction : i > index ? 0 : 1;
                context.drawImage(img, 0, 0, 1556, 1062);
                context.globalAlpha = 1;
            });
            drawSkin(palms);
            context.drawImage(bottle, 0, 0);
            context.drawImage(nailShadow, 0, 0, 1556, 1062);
            drawSkin(fingers);
            context.drawImage(nails, 0, 0);
            this.canvas.getContext('2d').clearRect(0, 0, 1556, 1062);
            this.canvas.getContext('2d').drawImage(frame, 0, 0);
            this.querySelector('[data-sku]').textContent = selected.sku;
            this.querySelector('[data-name]').textContent = selected.name || `Gelish shade ${selected.sku}`;
            const shop = this.querySelector('[data-shop]');
            shop.href = selected.url || `${this.dataset.searchUrl}?type=product&q=${encodeURIComponent(selected.sku)}`;
            shop.textContent = selected.url ? 'View product' : 'Find this shade';
            shop.hidden = false;
            this.status.textContent = 'Preview updated.';
            this.updateURL();
        } catch {
            if (version === this.paintVersion) this.status.textContent = 'This shade preview could not load. Select another shade or try again.';
        }
    }

    updateURL() {
        if (!this.selected) return;
        const url = new URL(location.href);
        url.searchParams.set('sku', this.selected.sku);
        url.searchParams.set('shape', this.shape);
        url.searchParams.set('length', this.querySelector('[data-length]').value);
        url.searchParams.set('skin', this.querySelector('[data-skin]').value);
        history.replaceState(null, '', url);
    }

    disconnectedCallback() {
        this.controller?.abort();
        this.paintVersion++;
    }
});
